import React, { useEffect, useState } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import { Icon, PaginationComponent } from "../../components/Component";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { handleGetApis, handlePostApis } from "../../service/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./abandonedCheckouts.css";

// Customers who filled in their details at checkout and pressed Continue, but
// never paid. One card per phone number (their latest attempt). Customers who
// paid later move to "Paid later" on their own.

const ITEMS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 400;

// Stat cards double as the filters.
const STATS = [
  { value: "new", label: "Not contacted", icon: "alert-circle", tone: "warning" },
  { value: "contacted", label: "Contacted", icon: "call", tone: "info" },
  { value: "not_interested", label: "Not interested", icon: "cross-circle", tone: "gray" },
  { value: "converted", label: "Paid later", icon: "check-circle", tone: "success" },
];
const SUMMARY_KEY = { new: "new", contacted: "contacted", not_interested: "not_interested", converted: "converted" };

const STATUS = {
  new: { label: "Not contacted", tone: "warning" },
  contacted: { label: "Contacted", tone: "info" },
  not_interested: { label: "Not interested", tone: "gray" },
};
const QUICK_NOTES = ["No answer", "Will pay today", "Asked about price", "Payment failed", "Wants a different size"];

const inr = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const formatINR = (v) => `₹${inr.format(Number(v) || 0)}`;

// Ages come from the DB (minutes), so they are right whatever the timezone.
const ago = (minutes) => {
  const m = Number(minutes);
  if (!Number.isFinite(m)) return "";
  if (m < 60) return `${Math.max(m, 1)} min ago`;
  const hours = Math.round(m / 60);
  if (hours < 24) return `${hours} hr${hours > 1 ? "s" : ""} ago`;
  const days = Math.round(hours / 24);
  if (days < 60) return `${days} day${days > 1 ? "s" : ""} ago`;
  return `${Math.round(days / 30)} months ago`;
};

const digits = (phone) => String(phone ?? "").replace(/\D/g, "").slice(-10);
const firstName = (name) => String(name ?? "").trim().split(/\s+/)[0] || "there";
const initial = (name) => String(name ?? "?").trim().charAt(0).toUpperCase() || "?";
const CHANNEL_LABEL = { whatsapp: "WhatsApp", email: "email" };

// Pre-filled message with the customer's own payment link: one tap to send.
const whatsappLink = (row) => {
  const first = row.items?.[0];
  const product = first ? `${first.product_name}${first.variant ? ` (${first.variant})` : ""}` : "your fragrance";
  const text =
    `Hi ${firstName(row.name)}, this is RYZHM. Your order for ${product} is saved, only the payment is left. ` +
    `You can complete it here: ${row.payment_link}`;
  return `https://wa.me/91${digits(row.phone)}?text=${encodeURIComponent(text)}`;
};

const ContactButtons = ({ row, onRemind, sending }) => (
  <div className="ac-contact-actions">
    <a href={`tel:+91${digits(row.phone)}`} className="ac-btn" title="Call">
      <Icon name="call" /> Call
    </a>
    {row.payment_link && !row.converted && (
      <>
        <button
          type="button"
          className="ac-btn ac-btn-whatsapp"
          onClick={() => onRemind(row, "whatsapp")}
          title="Send the payment link on WhatsApp"
        >
          <Icon name="whatsapp" /> WhatsApp
        </button>
        <button
          type="button"
          className="ac-btn"
          onClick={() => onRemind(row, "email")}
          disabled={!row.email || sending}
          title={row.email ? "Email the payment link" : "No email for this customer"}
        >
          <Icon name="mail" /> {sending ? "Sending…" : "Email"}
        </button>
        <button
          type="button"
          className="ac-btn ac-btn-icon"
          onClick={() => onRemind(row, "copy")}
          title="Copy payment link"
          aria-label="Copy payment link"
        >
          <Icon name="copy" />
        </button>
      </>
    )}
  </div>
);

const LeadCard = ({ row, onFollowup, onRemind, sending }) => {
  const status = row.converted ? { label: "Paid later", tone: "success" } : STATUS[row.followup_status];
  const itemCount = row.items.reduce((n, i) => n + Number(i.qty), 0);
  return (
    <div className={`ac-lead tone-${status.tone}`}>
      <div className="ac-person">
        <span className="ac-avatar">{initial(row.name)}</span>
        <div style={{ minWidth: 0 }}>
          <span className="ac-name">{row.name}</span>
          <span className="ac-meta">{row.email}</span>
          <span className="ac-meta">
            <Icon name="map-pin" /> {row.city} - {row.pincode}
          </span>
        </div>
      </div>

      <div className="ac-contact">
        <span className="ac-phone">+91 {digits(row.phone)}</span>
        <ContactButtons row={row} onRemind={onRemind} sending={sending} />
      </div>

      <div className="ac-items">
        {row.items.map((i, idx) => (
          <span key={idx} className="ac-item" title={`${i.product_name} ${i.variant ?? ""}`}>
            {i.product_name}
            {i.variant ? ` · ${i.variant}` : ""} × {i.qty}
          </span>
        ))}
        <span className="ac-value">{formatINR(row.total_amount)}</span>
        <span className="ac-meta">
          {itemCount} item{itemCount !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="ac-when">
        <div className="ac-mobile-items">
          {row.items.map((i, idx) => (
            <span key={idx} className="ac-item">
              {i.product_name}
              {i.variant ? ` · ${i.variant}` : ""} × {i.qty}
            </span>
          ))}
        </div>
        <span className="ac-time">
          <Icon name="clock" /> Left {ago(row.minutes_ago)}
        </span>
        <div className="ac-badges">
          <span className={`ac-badge tone-${status.tone}`}>{status.label}</span>
          {row.attempts > 1 && <span className="ac-badge ac-badge-attempts">{row.attempts} attempts</span>}
        </div>
        {row.reminder_count > 0 && (
          <span className="ac-reminder">
            <Icon name="send" /> Link sent {row.reminder_count}× · last by {CHANNEL_LABEL[row.last_reminder_channel]},{" "}
            {ago(row.reminder_minutes_ago)}
          </span>
        )}
        {row.followup_note && <span className="ac-note">“{row.followup_note}”</span>}
      </div>

      <div className="ac-lead-action">
        {!row.converted && (
          <button type="button" className="ac-btn ac-btn-primary" onClick={() => onFollowup(row)}>
            <Icon name="edit" /> Follow up
          </button>
        )}
      </div>
    </div>
  );
};

const AbandonedCheckouts = () => {
  const [filter, setFilter] = useState("new");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [list, setList] = useState({ key: null, rows: [], total: 0, summary: null, error: "" });

  const [editing, setEditing] = useState(null); // row being followed up
  const [form, setForm] = useState({ status: "contacted", note: "" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [sendingId, setSendingId] = useState(null); // order whose email is sending

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const key = `${filter}|${search}|${page}|${reload}`;

  // Each response is stored with the query it answers, so a slow old response
  // can never replace a newer one.
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ filter, page: String(page), limit: String(ITEMS_PER_PAGE) });
    if (search) params.set("search", search);
    handleGetApis(`admin/abandoned-checkouts?${params}`).then((res) => {
      if (cancelled) return;
      const ok = res?.data?.success;
      setList({
        key,
        rows: ok ? res.data.data : [],
        total: ok ? res.data.pagination.total : 0,
        summary: ok ? res.data.summary : null,
        error: ok ? "" : "Couldn't load abandoned checkouts. Please refresh.",
      });
    });
    return () => {
      cancelled = true;
    };
  }, [key, filter, page, search]);

  const loading = list.key !== key;
  const summary = list.summary;

  const chooseFilter = (value) => {
    setFilter(value);
    setPage(1);
  };

  const openFollowup = (row) => {
    setEditing(row);
    setForm({ status: row.followup_status === "new" ? "contacted" : row.followup_status, note: row.followup_note || "" });
    setSaveError("");
  };

  const addQuickNote = (text) =>
    setForm((f) => ({ ...f, note: f.note ? `${f.note.replace(/\s+$/, "")}. ${text}` : text }));

  // WhatsApp opens right away (inside the click, so it isn't popup-blocked) and
  // is then logged; email is sent by the API; copy just copies the link.
  const remind = async (row, channel) => {
    if (channel === "copy") {
      try {
        await navigator.clipboard.writeText(row.payment_link);
        toast.success("Payment link copied");
      } catch {
        window.prompt("Copy the payment link:", row.payment_link);
      }
      return;
    }
    if (channel === "whatsapp") window.open(whatsappLink(row), "_blank", "noopener,noreferrer");
    else setSendingId(row.id);

    const res = await handlePostApis(`admin/abandoned-checkouts/${row.id}/reminder`, { channel });
    if (channel === "email") setSendingId(null);
    if (res?.data?.success) {
      if (channel === "email") toast.success(`Payment link emailed to ${row.email}`);
      setReload((n) => n + 1);
    } else {
      toast.error(res?.data?.message || "Couldn't send the reminder");
    }
  };

  const saveFollowup = async () => {
    setSaving(true);
    setSaveError("");
    const res = await handlePostApis(`admin/abandoned-checkouts/${editing.id}/followup`, form);
    setSaving(false);
    if (!res?.data?.success) {
      setSaveError(res?.data?.message || "Couldn't save. Please try again.");
      return;
    }
    setEditing(null);
    setReload((n) => n + 1);
  };

  const activeLabel = filter === "all" ? "All open" : STATS.find((s) => s.value === filter)?.label;

  return (
    <React.Fragment>
      <Head title="Abandoned Checkouts" />
      <Content>
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="ac-page">
          <div className="ac-head">
            <div>
              <h3 className="ac-title">Abandoned Checkouts</h3>
              <p className="ac-sub">
                Customers who filled in their details but didn&apos;t pay within 15 minutes. Send them their payment
                link on WhatsApp or email; once they pay, they leave this list on their own.
              </p>
            </div>
            <div className="ac-head-side">
              <div className="ac-open-value">
                <span>Open value</span>
                <strong>{summary ? formatINR(summary.open_value) : "—"}</strong>
              </div>
              <button
                type="button"
                className="ac-icon-btn"
                onClick={() => setReload((n) => n + 1)}
                aria-label="Refresh"
                title="Refresh"
              >
                <Icon name="reload" />
              </button>
            </div>
          </div>

          <div className="ac-stats">
            {STATS.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`ac-stat tone-${s.tone} ${filter === s.value ? "is-active" : ""}`}
                onClick={() => chooseFilter(s.value)}
                aria-pressed={filter === s.value}
              >
                <span className="ac-stat-icon">
                  <Icon name={s.icon} />
                </span>
                <span>
                  <span className="ac-stat-count">{summary ? summary[SUMMARY_KEY[s.value]] : "–"}</span>
                  <span className="ac-stat-label">{s.label}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="ac-toolbar">
            <div className="ac-search">
              <Icon name="search" />
              <input
                type="search"
                className="form-control"
                placeholder="Search name, phone, email or order..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search"
              />
            </div>
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              {!loading && !list.error && (
                <span className="ac-result-count">
                  {list.total} in {activeLabel}
                </span>
              )}
              <button
                type="button"
                className={`ac-chip ${filter === "all" ? "is-active" : ""}`}
                onClick={() => chooseFilter("all")}
              >
                All open
              </button>
            </div>
          </div>

          {loading ? (
            <div className="ac-list" aria-busy="true">
              {[1, 2, 3].map((i) => (
                <div key={i} className="ac-skeleton" />
              ))}
            </div>
          ) : list.error ? (
            <div className="ac-empty ac-error">{list.error}</div>
          ) : list.rows.length === 0 ? (
            <div className="ac-empty">
              <Icon name="cart" />
              <h6>{search ? "No matches" : "All clear"}</h6>
              <p className="mb-0 fs-13px">
                {search ? `Nothing matches "${search}" in ${activeLabel}.` : `No customers in ${activeLabel}.`}
              </p>
            </div>
          ) : (
            <div className="ac-list">
              {list.rows.map((row) => (
                <LeadCard
                  key={row.id}
                  row={row}
                  onFollowup={openFollowup}
                  onRemind={remind}
                  sending={sendingId === row.id}
                />
              ))}
            </div>
          )}

          {!loading && list.total > ITEMS_PER_PAGE && (
            <div className="mt-3">
              <PaginationComponent itemPerPage={ITEMS_PER_PAGE} totalItems={list.total} paginate={setPage} currentPage={page} />
            </div>
          )}
        </div>

        <Modal isOpen={editing !== null} toggle={() => setEditing(null)} centered>
          <ModalHeader toggle={() => setEditing(null)}>Follow up</ModalHeader>
          {editing && (
            <ModalBody className="ac-page">
              <div className="ac-modal-summary">
                <strong>{editing.name}</strong> · +91 {digits(editing.phone)}
                <div className="text-soft">
                  {editing.items.map((i) => `${i.product_name}${i.variant ? ` (${i.variant})` : ""} × ${i.qty}`).join(", ")}{" "}
                  · {formatINR(editing.total_amount)} · #{editing.order_ref}
                </div>
                <ContactButtons row={editing} onRemind={remind} sending={sendingId === editing.id} />
              </div>

              <label className="form-label">Status</label>
              <div className="ac-segment" role="radiogroup" aria-label="Follow-up status">
                {Object.entries(STATUS).map(([value, s]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={form.status === value}
                    className={`tone-${s.tone} ${form.status === value ? "is-active" : ""}`}
                    onClick={() => setForm((f) => ({ ...f, status: value }))}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              <label className="form-label">Note</label>
              <div className="ac-quick-notes">
                {QUICK_NOTES.map((n) => (
                  <button key={n} type="button" onClick={() => addQuickNote(n)}>
                    + {n}
                  </button>
                ))}
              </div>
              <textarea
                className="form-control"
                rows={3}
                maxLength={500}
                placeholder="What did the customer say?"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              />
              {saveError && <div className="text-danger fs-13px mt-2">{saveError}</div>}
            </ModalBody>
          )}
          <ModalFooter>
            <Button color="light" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button color="primary" onClick={saveFollowup} disabled={saving}>
              {saving ? "Saving..." : "Save follow-up"}
            </Button>
          </ModalFooter>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default AbandonedCheckouts;
